<template>
  <section class="rich-text-section">
    <div class="container" v-html="sanitizedContent"></div>
  </section>
</template>

<script setup>
const props = defineProps({ content: { type: String, default: '' } })

// Strict allow-list HTML sanitiser. Stripping <script> alone leaves all the
// other XSS vectors wide open: <img alt="" src=x onerror>, <iframe>, <object>,
// javascript: / data: URLs, on*= event-handler attributes, <svg> with
// embedded scripts, etc. We keep only a small set of formatting elements
// and harmless attributes; everything else is dropped.
const ALLOWED_TAGS = new Set([
  'p', 'br', 'strong', 'em', 'b', 'i', 'u', 's',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'a', 'img',
  'blockquote', 'code', 'pre',
  'span', 'div'
])
const ALLOWED_ATTRS = {
  a: new Set(['href', 'title', 'target', 'rel']),
  img: new Set(['src', 'alt', 'title', 'width', 'height']),
  '*': new Set(['class'])
}
function isSafeUrl(url) {
  if (!url) return false
  const trimmed = String(url).trim().toLowerCase()
  if (trimmed.startsWith('javascript:')) return false
  if (trimmed.startsWith('data:') && !trimmed.startsWith('data:image/')) return false
  if (trimmed.startsWith('vbscript:')) return false
  return true
}
function sanitizeHtml(html) {
  if (!html || typeof window === 'undefined') {
    // SSR fallback: strip every tag entirely. The block re-hydrates on the
    // client and the parsed/sanitised version replaces this immediately.
    return String(html || '').replace(/<[^>]*>/g, '')
  }
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html')
  const root = doc.body.firstChild
  if (!root) return ''
  const walk = (node) => {
    const children = Array.from(node.childNodes)
    for (const child of children) {
      if (child.nodeType === 3) continue // text node — keep
      if (child.nodeType !== 1) { child.remove(); continue } // drop comments / cdata
      const tag = child.tagName.toLowerCase()
      if (!ALLOWED_TAGS.has(tag)) {
        // Unwrap unknown tags so harmless content survives but the wrapper
        // (and any dangerous handlers attached to it) is gone.
        while (child.firstChild) node.insertBefore(child.firstChild, child)
        child.remove()
        continue
      }
      // Strip every attribute that isn't on the allow-list.
      const allowedForTag = ALLOWED_ATTRS[tag] || new Set()
      const allowedGlobal = ALLOWED_ATTRS['*']
      for (const attr of Array.from(child.attributes)) {
        const name = attr.name.toLowerCase()
        if (!allowedForTag.has(name) && !allowedGlobal.has(name)) {
          child.removeAttribute(attr.name)
          continue
        }
        if ((name === 'href' || name === 'src') && !isSafeUrl(attr.value)) {
          child.removeAttribute(attr.name)
          continue
        }
      }
      if (tag === 'a') {
        // Force safe rel on external links so we don't leak window.opener.
        child.setAttribute('rel', 'noopener noreferrer nofollow')
        if (!child.hasAttribute('target')) child.setAttribute('target', '_blank')
      }
      walk(child)
    }
  }
  walk(root)
  return root.innerHTML
}

const sanitizedContent = computed(() => sanitizeHtml(props.content))
</script>

<style scoped>
.rich-text-section {
  padding: 80px 0;
}

.container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 24px;
  color: var(--text-muted);
  line-height: 1.8;
}

.container :deep(h1),
.container :deep(h2),
.container :deep(h3) {
  color: var(--text-main);
  font-family: var(--font-heading);
  margin-bottom: 16px;
}

.container :deep(h1) { font-size: 2.5rem; }
.container :deep(h2) { font-size: 2rem; }
.container :deep(h3) { font-size: 1.5rem; }

.container :deep(p) { margin-bottom: 16px; }

.container :deep(a) {
  color: var(--primary);
  text-decoration: underline;
}

.container :deep(ul), .container :deep(ol) {
  padding-left: 24px;
  margin-bottom: 16px;
}

.container :deep(img) {
  max-width: 100%;
  border-radius: var(--radius-md);
}
</style>
